import React, { useState } from 'react';
import { animated as a, useSpring } from 'react-spring';
import { useT } from '../../i18n/LanguageProvider';
import { prefersReducedMotion } from './SingleCardViewer';

interface Props {
	pages: Array<() => JSX.Element>;
}

const faceStyle: React.CSSProperties = {
	backfaceVisibility: 'hidden',
	WebkitBackfaceVisibility: 'hidden',
	boxShadow: '0 18px 30px rgba(0, 0, 0, 0.2)',
};

// Visibility is derived from the angle so Safari cannot leak the hidden face's canvas through backface-visibility.
const frontShown = (r: number) => {
	const m = ((r % 360) + 360) % 360;
	return m < 90 || m > 270;
};

/** A two-page postcard: click (or Enter) flips between the front and the back. */
const PostcardViewer = ({ pages }: Props) => {
	const { t } = useT();
	const [flipped, setFlipped] = useState(false);
	const { rotateY } = useSpring({
		rotateY: flipped ? 180 : 0,
		config: { tension: 170, friction: 22 },
		immediate: prefersReducedMotion(),
	});
	const toggle = () => setFlipped((f) => !f);
	const frontTransform = rotateY.to((r) => `perspective(1200px) rotateY(${r}deg)`);
	const frontVisibility = rotateY.to((r) => (frontShown(r) ? 'visible' : 'hidden'));
	const backVisibility = rotateY.to((r) => (frontShown(r) ? 'hidden' : 'visible'));
	const backTransform = rotateY.to((r) => `perspective(1200px) rotateY(${r + 180}deg)`);

	return (
		<div
			role="button"
			tabIndex={0}
			aria-label={t(flipped ? 'viewcard.postcardBack' : 'viewcard.postcardFront')}
			aria-pressed={flipped}
			className="relative cursor-pointer"
			onClick={toggle}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggle();
				}
			}}
		>
			<a.div style={{ ...faceStyle, transform: frontTransform, visibility: frontVisibility }}>{pages[0]?.()}</a.div>
			<a.div style={{ ...faceStyle, position: 'absolute', top: 0, left: 0, transform: backTransform, visibility: backVisibility }}>
				{pages[1]?.()}
			</a.div>
		</div>
	);
};

export default PostcardViewer;
